import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import successIcon from "../../../assets/toast-icons/icons8-success.svg";
import errorIcon from "../../../assets/toast-icons/icons8-error.svg";
import infoIcon from "../../../assets/toast-icons/icons8-info.svg";
import warningIcon from "../../../assets/toast-icons/icons8-warning.svg";

const icons = {
  success: (
    <img src={successIcon} alt="Success" className="mr-2 w-6 h-6 shrink-0" />
  ),
  info: <img src={infoIcon} alt="Info" className="mr-2 w-6 h-6 shrink-0" />,
  warning: (
    <img src={warningIcon} alt="Warning" className="mr-2 w-6 h-6 shrink-0" />
  ),
  error: <img src={errorIcon} alt="Error" className="mr-2 w-6 h-6 shrink-0" />,
};

// Type-specific Tailwind classes - matching react-hot-toast styling
const typeClasses = {
  success: "text-green-600",
  info: "text-blue-600",
  warning: "text-yellow-600",
  error: "text-red-600",
};

const Notification = ({
  id,
  type = "info",
  message,
  duration,
  onClose,
  position,
}) => {
  const [animationState, setAnimationState] = useState("entering");

  useEffect(() => {
    const enterTimeout = setTimeout(() => setAnimationState("visible"), 10);
    const closeTimeout = setTimeout(() => {
      setAnimationState("exiting");
      setTimeout(() => onClose(id), 400);
    }, duration);

    return () => {
      clearTimeout(enterTimeout);
      clearTimeout(closeTimeout);
    };
  }, [id, duration, onClose]);

  const baseClasses =
    "flex items-center p-3 rounded-md shadow-lg w-72 transition-all duration-300 ease-in-out bg-white text-gray-800";

  let enteringClass = "";

  if (position.includes("left")) {
    enteringClass = "opacity-0 -translate-x-full";
  } else if (position.includes("center")) {
    enteringClass = "opacity-0 translate-y-full";
  } else {
    enteringClass = "opacity-0 translate-x-full";
  }

  const animationClasses = {
    entering: enteringClass,
    visible: "opacity-100 translate-x-0 translate-y-0",
    exiting: "opacity-0 scale-95",
  };

  const ariaRole = type === "error" || type === "warning" ? "alert" : "status";

  return (
    <div
      role={ariaRole}
      aria-live="assertive"
      className={`${baseClasses} ${animationClasses[animationState]}`}
    >
      <div className={`${typeClasses[type]}`}>{icons[type]}</div>
      <span className="flex-grow text-gray-800 text-sm ml-2">{message}</span>
    </div>
  );
};

Notification.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string,
  message: PropTypes.string.isRequired,
  duration: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  position: PropTypes.string,
};

export default Notification;
